import { QueryClient } from '@tanstack/react-query';

import { YourAccountFormValues } from '@/components/forms/YourAccountForm/index.types';
import { YourAccountFormV2Values } from '@/components/forms/YourAccountFormV2/index.types';
import { useUserRegistrationContext } from '@/context/UserRegistrationContext';

type UserRegistrationContext = ReturnType<typeof useUserRegistrationContext>;

type HandlerDependencies = {
  queryClient: QueryClient;
  setAccountFields: UserRegistrationContext['setAccountFields'];
  setScopedToken: UserRegistrationContext['setScopedToken'];
  mutateVerifyIdentity: (params: {
    input: { accountNumber: string; postcode: string; familyName: string };
  }) => void;
  mutateVerifyIdentityFallback: (params: {
    input: { postcode: string; firstLineOfAddress: string; familyName: string };
  }) => void;
};

export const createSubmitHandlers = ({
  queryClient,
  setAccountFields,
  setScopedToken,
  mutateVerifyIdentity,
  mutateVerifyIdentityFallback,
}: HandlerDependencies) => {
  const handleSubmit = (values: YourAccountFormValues) => {
    // Clear blackhole data from cache to stop the blackhole email account user query. This means the cache is empty and the query will be made again with the new values.
    queryClient.invalidateQueries({ queryKey: ['accountIdentification'] });

    const { accountNumber, postcode, surname, billingAddress } = values;

    // Set account field values in context
    setAccountFields({
      accountNumber,
      postcode,
      surname,
      billingAddress,
    });

    // Resets the scopedToken for a fresh search
    setScopedToken('');

    // mutation for authentication
    mutateVerifyIdentity({
      input: { accountNumber, postcode, familyName: surname },
    });
  };

  // For V2 Register For Online Account journey (Without account number)
  const handleSubmitV2 = async (values: YourAccountFormV2Values) => {
    // Clear blackhole data from cache to stop the blackhole email account user query. This means the cache is empty and the query will be made again with the new values.
    await queryClient.invalidateQueries({
      queryKey: ['accountIdentification'],
    });
    await queryClient.invalidateQueries({
      queryKey: ['userRegistrationStatus'],
    });

    const { postcode, address, surname } = values;

    if (!address) {
      return;
    }

    // Extract just the first line of the address (up to the first comma)
    const firstLineOnly = String(address.value).split(',')[0].trim();

    // Set account field values in context
    setAccountFields({
      accountNumber: '',
      postcode,
      surname,
      billingAddress: address,
    });

    // Resets the scopedToken for a fresh search
    setScopedToken('');

    // mutation for authentication
    mutateVerifyIdentityFallback({
      input: {
        postcode,
        firstLineOfAddress: firstLineOnly,
        familyName: surname,
      },
    });
  };

  return {
    handleSubmit,
    handleSubmitV2,
  };
};
